import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Autocomplete,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import StandardDataTable, { Column } from '../StandardDataTable';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { useNotification } from '../../providers/NotificationProvider';
import { useConfirm } from '../../providers/ConfirmProvider';
import {
  mailApi,
  MailList,
  MailListMember,
  SubscriptionStatus,
  SUBSCRIPTION_STATUS_LABEL,
  CustomerHit,
} from '../../hooks/useMailDelivery';

/**
 * 配信リストの中身 (読者) を見て調整するダイアログ。配信作成の「配信先」ステップから開く。
 *
 * - 一覧 顧客名・宛名・拠点名・メール・状態・所属リスト。検索と状態での絞り込み、ページング
 * - 行操作 停止 / 再開 / このリストから外す / 宛名・拠点名・メールの修正
 * - 読者を追加 CRM の顧客を検索して選ぶ。見つからなければ法人として新規登録してから追加する
 *   (CRM を軸にする方針のため、顧客に紐づかない読者は作らない)
 */

const fieldSx = { '& .MuiInputBase-input': { fontSize: '13px' }, '& .MuiInputLabel-root': { fontSize: '13px' } };
const titleSx = { fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, color: blueGrey[600], borderBottom: '1px solid #f1f5f9' };

const STATUS_COLOR: Record<SubscriptionStatus, 'success' | 'default' | 'error' | 'warning'> = {
  active: 'success',
  stopped: 'default',
  bounced: 'error',
  unsubscribed: 'warning',
};

// ---------------------------------------------------------------- 読者の追加
function AddMemberDialog({ open, list, operator, onClose, onAdded }: {
  open: boolean;
  list: MailList;
  operator: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const notify = useNotification();
  const [isNew, setIsNew] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<CustomerHit[]>([]);
  const [customer, setCustomer] = useState<CustomerHit | null>(null);
  const [company, setCompany] = useState({ company_name: '', customer_branch_name: '', tel_number: '', address_main: '' });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [branch, setBranch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIsNew(false); setQuery(''); setOptions([]); setCustomer(null);
    setCompany({ company_name: '', customer_branch_name: '', tel_number: '', address_main: '' });
    setEmail(''); setName(''); setBranch('');
  }, [open]);

  // 顧客検索 (入力が止まって 400ms 後)
  useEffect(() => {
    if (isNew || query.trim().length < 2) {
      setOptions([]);
      return;
    }
    const t = window.setTimeout(async () => {
      const r = await mailApi.searchCustomers(query.trim());
      if (r.ok) setOptions(r.data.results || []);
    }, 400);
    return () => window.clearTimeout(t);
  }, [query, isNew]);

  const canSave = email.includes('@') && (isNew ? company.company_name.trim() !== '' : customer != null);

  const handleSave = async () => {
    setSaving(true);
    const r = await mailApi.addMember({
      list_code: list.list_code,
      email: email.trim(),
      recipient_name: name.trim() || null,
      recipient_branch: branch.trim() || null,
      customer_id: isNew ? null : customer?.id,
      new_corporate: isNew ? company : null,
    }, operator);
    setSaving(false);
    if (!r.ok) return notify.error(r.error);
    notify.success(r.data.created_customer ? '法人顧客を新規登録して読者に追加しました' : '読者に追加しました');
    r.data.warnings.forEach((w) => notify.warning(w));
    onAdded();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={titleSx}>
        <Icons.PersonAdd sx={{ fontSize: 18 }} />
        読者を追加 ({list.list_name})
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <FormControlLabel
          control={<Switch size="small" checked={isNew} onChange={(e) => setIsNew(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13 }}>顧客管理に無い会社なので、法人として新規登録する</Typography>}
        />
        {!isNew ? (
          <Autocomplete
            size="small"
            options={options}
            value={customer}
            onChange={(_, v) => setCustomer(v)}
            inputValue={query}
            onInputChange={(_, v) => setQuery(v)}
            filterOptions={(x) => x}
            getOptionLabel={(o) => `${o.display_name || ''}${o.employer_name ? ` (${o.employer_name})` : ''}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText={query.trim().length < 2 ? '2 文字以上入力してください' : '見つかりません (上のスイッチで新規登録できます)'}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography sx={{ fontSize: 13 }}>{o.display_name}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {o.customer_type === 'corporate' ? '法人' : `個人${o.employer_name ? ` (勤務先 ${o.employer_name})` : ''}`}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="顧客を検索 (会社名・拠点名・氏名)" required sx={fieldSx} />}
          />
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField size="small" required label="会社名" value={company.company_name}
              onChange={(e) => setCompany({ ...company, company_name: e.target.value })} sx={{ ...fieldSx, gridColumn: '1 / -1' }} />
            <TextField size="small" label="拠点名 (支店・店舗)" value={company.customer_branch_name}
              onChange={(e) => setCompany({ ...company, customer_branch_name: e.target.value })} sx={fieldSx} />
            <TextField size="small" label="電話番号" value={company.tel_number}
              onChange={(e) => setCompany({ ...company, tel_number: e.target.value })} sx={fieldSx} />
            <TextField size="small" label="住所" value={company.address_main}
              onChange={(e) => setCompany({ ...company, address_main: e.target.value })} sx={{ ...fieldSx, gridColumn: '1 / -1' }} />
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', gridColumn: '1 / -1' }}>
              顧客管理システムに法人顧客として登録されます (登録経路 mail_delivery)。
            </Typography>
          </Box>
        )}
        <TextField size="small" required label="メールアドレス" value={email} onChange={(e) => setEmail(e.target.value)} sx={fieldSx} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <TextField size="small" label="宛名 (担当者名)" value={name} onChange={(e) => setName(e.target.value)} sx={fieldSx} />
          <TextField size="small" label="宛先の拠点名" value={branch} onChange={(e) => setBranch(e.target.value)} sx={fieldSx} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
        <Button onClick={handleSave} disabled={!canSave || saving} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>
          {saving ? '登録中...' : '追加する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------- 宛名・メールの修正
function EditMemberDialog({ member, operator, onClose, onSaved }: {
  member: MailListMember | null;
  operator: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const notify = useNotification();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [branch, setBranch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!member) return;
    setEmail(member.email); setName(member.recipient_name || ''); setBranch(member.recipient_branch || '');
  }, [member]);

  const handleSave = async () => {
    if (!member) return;
    setSaving(true);
    const r = await mailApi.updateMember({ subscription_id: member.subscription_id, action: 'edit', email, recipient_name: name, recipient_branch: branch }, operator);
    setSaving(false);
    if (!r.ok) return notify.error(r.error);
    notify.success('読者の情報を更新しました');
    onSaved();
    onClose();
  };

  return (
    <Dialog open={member != null} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={titleSx}>
        <Icons.Edit sx={{ fontSize: 18 }} />
        読者の編集
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{member?.customer_name}</Typography>
        <TextField size="small" label="メールアドレス" value={email} onChange={(e) => setEmail(e.target.value)} sx={fieldSx} />
        <TextField size="small" label="宛名 (担当者名)" value={name} onChange={(e) => setName(e.target.value)} sx={fieldSx} />
        <TextField size="small" label="宛先の拠点名" value={branch} onChange={(e) => setBranch(e.target.value)} sx={fieldSx} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
        <Button onClick={handleSave} disabled={saving || !email.includes('@')} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>
          保存する
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------- 本体
type Props = {
  list: MailList | null;
  operator: string;
  onClose: () => void;
  /** 読者を変更したとき (配信対象の件数を数え直すため) */
  onChanged: () => void;
};

const MailListMembersDialog: React.FC<Props> = ({ list, operator, onClose, onChanged }) => {
  const notify = useNotification();
  const confirm = useConfirm();
  const [rows, setRows] = useState<MailListMember[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [status, setStatus] = useState<string>('active');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<MailListMember | null>(null);

  const reload = useCallback(async () => {
    if (!list) return;
    setLoading(true);
    const r = await mailApi.listMembers(list.list_code, page, pageSize, q, status);
    if (r.ok) {
      setRows(r.data.rowsResult || []);
      setTotal(r.data.total || 0);
      setCounts(r.data.status_counts || {});
    } else {
      notify.error(r.error);
    }
    setLoading(false);
  }, [list, page, pageSize, q, status, notify]);

  // 開くたびに初期化
  useEffect(() => {
    if (!list) return;
    setPage(0); setStatus('active'); setQInput(''); setQ('');
  }, [list]);

  useEffect(() => {
    reload();
  }, [reload]);

  const changed = useCallback(() => {
    reload();
    onChanged();
  }, [reload, onChanged]);

  const act = useCallback(async (m: MailListMember, action: 'stop' | 'resume' | 'remove') => {
    if (!list) return;
    const label = action === 'stop' ? '配信を停止' : action === 'resume' ? '配信を再開' : `「${list.list_name}」から外す`;
    const ok = await confirm({
      title: label,
      message: `${m.customer_name || ''} ${m.recipient_name || ''}〈${m.email}〉を${label}しますか？`
        + (action === 'stop' ? '\nこの読者はすべての配信リストで配信対象外になります。' : '')
        + (action === 'remove' ? '\n他の配信リストへの所属はそのまま残ります。' : ''),
      confirmText: action === 'resume' ? '再開する' : action === 'stop' ? '停止する' : '外す',
      destructive: action !== 'resume',
    });
    if (!ok) return;
    const r = await mailApi.updateMember({ subscription_id: m.subscription_id, action, list_code: list.list_code }, operator);
    if (!r.ok) return notify.error(r.error);
    notify.success(action === 'stop' ? '配信を停止しました' : action === 'resume' ? '配信を再開しました' : 'リストから外しました');
    changed();
  }, [list, operator, confirm, notify, changed]);

  const columns: Column[] = useMemo(() => [
    { field: 'customer_name', headerName: '顧客', width: 230, renderCell: (r: MailListMember) => r.customer_name || '(顧客名なし)' },
    { field: 'recipient_name', headerName: '宛名', width: 110 },
    { field: 'recipient_branch', headerName: '拠点名', width: 130 },
    { field: 'email', headerName: 'メール', width: 230 },
    {
      field: 'status', headerName: '状態', width: 120,
      renderCell: (r: MailListMember) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip size="small" label={SUBSCRIPTION_STATUS_LABEL[r.status] ?? r.status} color={STATUS_COLOR[r.status] ?? 'default'}
            variant={r.status === 'active' ? 'filled' : 'outlined'} sx={{ fontSize: 11, height: 20 }} />
          {r.suppression_reason && (
            <Tooltip title={`送信禁止リストに登録 (${r.suppression_reason})`}>
              <Icons.Cancel sx={{ fontSize: 15, color: '#c62828' }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    { field: 'list_names', headerName: '所属リスト', width: 200 },
    {
      field: '_actions', headerName: '操作', width: 130,
      renderCell: (r: MailListMember) => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="宛名・メールを編集">
            <IconButton size="small" onClick={() => setEditing(r)}><Icons.Edit sx={{ fontSize: 16 }} /></IconButton>
          </Tooltip>
          {r.status === 'active' ? (
            <Tooltip title="配信を停止">
              <IconButton size="small" color="warning" onClick={() => act(r, 'stop')}><Icons.Cancel sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={r.status === 'unsubscribed' ? 'ご本人が解除したため再開できません' : '配信を再開'}>
              <span>
                <IconButton size="small" color="success" disabled={r.status === 'unsubscribed'} onClick={() => act(r, 'resume')}>
                  <Icons.PlayArrow sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="このリストから外す">
            <IconButton size="small" color="error" onClick={() => act(r, 'remove')}><Icons.Delete sx={{ fontSize: 16 }} /></IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [act]);

  const filters: { key: string; label: string }[] = [
    { key: 'active', label: '配信中' },
    { key: 'stopped', label: '停止' },
    { key: 'bounced', label: 'エラー停止' },
    { key: 'unsubscribed', label: '解除' },
    { key: '', label: 'すべて' },
  ];
  const allCount = ['active', 'stopped', 'bounced', 'unsubscribed'].reduce((a, k) => a + (counts[k] || 0), 0);

  return (
    <>
      <Dialog open={list != null} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '86vh' } }}>
        <DialogTitle sx={titleSx}>
          <Icons.People sx={{ fontSize: 18 }} />
          配信リストの中身 {list?.list_name}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            {filters.map((f) => (
              <Chip key={f.key || 'all'} size="small"
                label={`${f.label} ${(f.key ? counts[f.key] || 0 : allCount).toLocaleString()}`}
                variant={status === f.key ? 'filled' : 'outlined'} color={status === f.key ? 'primary' : 'default'}
                onClick={() => { setStatus(f.key); setPage(0); }} sx={{ fontSize: 12 }} />
            ))}
            <TextField size="small" placeholder="会社名 / 宛名 / メールで検索" value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setQ(qInput.trim()); setPage(0); } }}
              onBlur={() => { setQ(qInput.trim()); setPage(0); }}
              sx={{ width: 260, ml: 1, '& .MuiInputBase-input': { fontSize: 12.5, py: 0.6 } }} />
            <IconButton size="small" onClick={reload}><Icons.Refresh fontSize="small" /></IconButton>
            <Box sx={{ flex: 1 }} />
            <Button size="small" sx={STANDARD_CTA_BUTTON_SX} startIcon={<Icons.PersonAdd />} onClick={() => setAdding(true)}>
              読者を追加
            </Button>
          </Box>
          <Alert severity="info" sx={{ mb: 1, py: 0, fontSize: 12 }}>
            「停止」はすべての配信リストで配信対象外になります。「リストから外す」はこのリストだけ外し、他のリストには残ります。ご本人が解除した読者は再開できません。
          </Alert>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <StandardDataTable rows={rows.map((r) => ({ ...r, id: r.subscription_id }))} columns={columns} loading={loading}
              loadingMessage="読者を取得中" totalCount={total} page={page} pageSize={pageSize}
              onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(0); }} onSortChange={() => {}} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX}>閉じる</Button>
        </DialogActions>
      </Dialog>
      {list && <AddMemberDialog open={adding} list={list} operator={operator} onClose={() => setAdding(false)} onAdded={changed} />}
      <EditMemberDialog member={editing} operator={operator} onClose={() => setEditing(null)} onSaved={changed} />
    </>
  );
};

export default MailListMembersDialog;
